import { useGetRemainingTickets } from "lib/hooks/use-get-remaining-tickets";
import { AddToBasket } from "components/buttons/add-to-basket";
import { Button } from "components/buttons/button";
import Link from "next/link";
import { TicketContainer } from "./ticket-container";
import currency from "currency.js";
import { GetEventQueryResult } from "lib/graphql-generated";
import { useUser } from "lib/hooks/use-user";
import { Spinner } from "components/spinner";

interface Props {
  event: GetEventQueryResult["data"]["events"][0];
}

export const InPersonEventTickets = ({ event }: Props) => {
  const { userData } = useUser();
  const { isError, isLoading, ticketData } = useGetRemainingTickets(event.id, {
    refreshInterval: 30000,
  });

  return (
    <>
      <h3 className='pb-2 pt-4 px-3 text-lg header text-center col-span-full'>
        In Person Tickets
      </h3>
      {isLoading ? (
        <div className='w-full text-center flex flex-row space-x-4 justify-center'>
          <p>Getting Event Ticket Data </p>
          <Spinner />
        </div>
      ) : !!isError ? (
        <div className='w-full text-center flex flex-row space-x-4 justify-center text-red-800'>
          <p>
            There was an error fetching ticketing data. If this problem persists
            please contact us directly!
          </p>
        </div>
      ) : (
        <>
          <TicketContainer
            header='Buy Event Voucher'
            text={`Includes${" "}
                          ${currency(event.price, {
                            fromCents: true,
                            symbol: "£",
                          }).format()}${" "}
                          voucher redeemable against featured book(s) & helps support the bookshop in running events.`}
            button={
              ticketData.remainingSeats > 0 ? (
                <AddToBasket
                  className='whitespace-nowrap my-auto ml-auto mr-auto md:mr-0'
                  buttonType='secondary'
                  buttonSize='sm'
                  payload={{
                    type: "ticket-voucher",
                    id: `ticket-voucher-${event.id}`,
                    data: {
                      quantity: 1,
                      price: event.price,
                      event: {
                        event_id: event.id,
                        title: event.title,
                        image: event.image,
                      },
                    },
                  }}>
                  Add to basket{" "}
                  {currency(event.price, {
                    fromCents: true,
                    symbol: "£",
                  }).format()}
                </AddToBasket>
              ) : (
                <span className='my-auto ml-auto mr-auto md:mr-0 bg-yellow-light px-4 rounded-md text-yellow-900'>
                  Sold out
                </span>
              )
            }
          />

          {event.event_books.length > 0 && (
            <TicketContainer
              header='Buy Book & Attend Event'
              text={`Includes attendance & a copy of the book`}
              button={
                ticketData.remainingSeats > 0 ? (
                  <AddToBasket
                    className='whitespace-nowrap my-auto ml-auto mr-auto md:mr-0'
                    buttonType='secondary'
                    buttonSize='sm'
                    payload={{
                      type: "ticket-book",
                      id: `ticket-book-${event.id}`,
                      data: {
                        event: {
                          event_id: event.id,
                          title: event.title,
                          image: event.image,
                        },
                        book: {
                          title: event.event_books[0].book.title,
                          isbn: event.event_books[0].book.isbn,
                          image: event.event_books[0].book.image,
                        },
                        price: event.event_books[0].book.price,
                        discount: event.event_books[0].book.is_discounted
                          ? event.event_books[0].book.discount_value
                          : null,
                        quantity: 1,
                      },
                    }}>
                    Add to basket{" "}
                    {currency(event.event_books[0].book.price, {
                      fromCents: true,
                      symbol: "£",
                    }).format()}
                  </AddToBasket>
                ) : (
                  <span className='my-auto ml-auto mr-auto md:mr-0 bg-yellow-light px-4 rounded-md text-yellow-900'>
                    Sold out
                  </span>
                )
              }
            />
          )}

          <TicketContainer
            header='Reserve Free Seat'
            text={`Please do book a free place if you're broke.`}
            button={
              ticketData.remaningFreeSeats > 0 ? (
                !!userData?.accountId ? (
                  <AddToBasket
                    className='whitespace-nowrap my-auto ml-auto mr-auto md:mr-0'
                    buttonType='secondary'
                    buttonSize='sm'
                    payload={{
                      type: "ticket-free",
                      id: `ticket-free-${event.id}`,
                      data: {
                        quantity: 1,
                        event: {
                          event_id: event.id,
                          title: event.title,
                          image: event.image,
                        },
                      },
                    }}>
                    Add to Basket
                  </AddToBasket>
                ) : (
                  <Button
                    buttonType='secondary'
                    href='/signin'
                    className='whitespace-nowrap my-auto ml-auto mr-auto md:mr-0'>
                    Login to reserve
                  </Button>
                )
              ) : (
                <span className='my-auto ml-auto mr-auto md:mr-0 bg-yellow-light px-4 rounded-md text-yellow-900'>
                  Sold out
                </span>
              )
            }
          />
        </>
      )}
    </>
  );
};
